import React from "react";
import { SidebarNav } from "./SidebarNav";
import { SidebarShareCard } from "./SidebarShareCard";
import { DesktopTopBar } from "./DesktopTopBar";
import { useReferrals } from "@/providers/ReferralsProvider";
import type { DesktopLayoutProps } from "./DesktopLayout.types";

/**
 * Desktop layout from the redesign: glass sidebar on the left, then the top
 * bar, the two-tone page title and the page content (with an optional right
 * column next to it).
 */
export const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  children,
  pageTitle,
  pageTitleAccent,
  hideTitle = false,
  pageDescription,
  rightSidebar,
  showRightSidebar = !!rightSidebar,
  contentClassName = "",
  headerActions,
  search,
}) => {
  const { data } = useReferrals();
  const referralLink = data?.referralStats?.referralLink || null;

  // Two-tone title: the last word is brand green.
  const lastSpace = pageTitle.lastIndexOf(" ");
  const titleLead = pageTitleAccent
    ? `${pageTitle} `
    : lastSpace > 0
      ? pageTitle.slice(0, lastSpace + 1)
      : pageTitle;
  const titleAccent = pageTitleAccent ?? (lastSpace > 0 ? pageTitle.slice(lastSpace + 1) : "");

  return (
    <div className="flex h-screen gap-8 p-4">
      <SidebarNav bottomContent={<SidebarShareCard referralLink={referralLink} />} />

      <main
        className={`flex min-w-0 flex-1 flex-col gap-8 overflow-y-auto pr-1 [scrollbar-gutter:stable] ${contentClassName}`}
      >
        <DesktopTopBar search={search} />

        {!hideTitle && (
        <div className="flex items-end justify-between gap-6">
          <div>
            <h1 className="text-5xl font-semibold leading-[60px] tracking-[-0.96px] text-white">
              {titleLead}
              {titleAccent && <span className="text-accent">{titleAccent}</span>}
            </h1>
            {pageDescription && (
              <p className="mt-0.5 text-base leading-6 text-[#a3a3a3]">{pageDescription}</p>
            )}
          </div>
          {headerActions && <div className="flex items-center gap-3">{headerActions}</div>}
        </div>
        )}

        <div className="flex min-h-0 items-start gap-8 pb-4">
          <div className="min-w-0 flex-1">{children}</div>
          {showRightSidebar && rightSidebar && (
            <aside className="sticky top-0 hidden shrink-0 min-[1400px]:block">{rightSidebar}</aside>
          )}
        </div>
      </main>
    </div>
  );
};
